import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { computeMastersPathFromEloPoints, upsertMyNickname } from '../lib/supabaseProfile';
import { fetchPuzzlePanelStats } from '../lib/supabasePuzzles';
import {
  fetchMySubscriptionDetails,
  getPlanLabel,
  getPlanUiCards,
  manageMyPaddleSubscription
} from '../lib/supabaseSubscription';
import { canUsePaddleCheckout, openPlanCheckout } from '../lib/paddleCheckout';
import { changeMyPassword, logout } from '../lib/supabaseAuth';

function AccountPage({
  language,
  user,
  profile,
  initialTab = 'overview',
  onBack,
  onRequestLogin,
  onProfileUpdated,
  onLanguageChange,
  onLogout
}) {
  const isRo = language === 'ro';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [puzzleStats, setPuzzleStats] = useState({
    puzzlePoints: 0,
    puzzleRating: 500,
    rank: null,
    solved: 0,
    attempts: 0,
    accuracy: 0
  });
  const [currentPlan, setCurrentPlan] = useState('explorer');
  const [planStatus, setPlanStatus] = useState('active');
  const [planProvider, setPlanProvider] = useState('');
  const [planExternalSubscriptionId, setPlanExternalSubscriptionId] = useState('');
  const [planPeriodEnd, setPlanPeriodEnd] = useState(null);
  const [planCancelAtPeriodEnd, setPlanCancelAtPeriodEnd] = useState(false);
  const [checkoutBusyPlan, setCheckoutBusyPlan] = useState('');
  const [manageBusy, setManageBusy] = useState('');
  const [checkoutMsg, setCheckoutMsg] = useState('');
  const [checkoutErr, setCheckoutErr] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [securityBusy, setSecurityBusy] = useState(false);
  const [securityMsg, setSecurityMsg] = useState('');
  const [securityErr, setSecurityErr] = useState('');

  const refreshSubscription = useCallback(async () => {
    if (!user?.id) return;
    const plan = await fetchMySubscriptionDetails();
    setCurrentPlan(String(plan?.planCode || 'explorer'));
    setPlanStatus(String(plan?.status || 'active'));
    setPlanProvider(String(plan?.provider || ''));
    setPlanExternalSubscriptionId(String(plan?.externalSubscriptionId || ''));
    setPlanPeriodEnd(plan?.periodEnd || null);
    setPlanCancelAtPeriodEnd(Boolean(plan?.cancelAtPeriodEnd));
  }, [user?.id]);

  const nickname = useMemo(() => {
    const fromProfile = (profile && (profile.nickname || profile.display_name)) || '';
    const fromMeta = (user && (user.user_metadata?.nickname || user.user_metadata?.display_name)) || '';
    const fromEmail = user && user.email ? String(user.email).split('@')[0] : '';
    return String(fromProfile || fromMeta || fromEmail || '').trim();
  }, [profile, user]);

  useEffect(() => {
    setNicknameDraft(String(nickname || '').trim());
  }, [nickname]);

  useEffect(() => {
    const allowedTabs = new Set(['overview', 'progress', 'puzzles', 'billing', 'security']);
    setActiveTab(allowedTabs.has(initialTab) ? initialTab : 'overview');
  }, [initialTab]);

  useEffect(() => {
    let alive = true;
    async function loadPuzzle() {
      if (!user?.id) return;
      try {
        const stats = await fetchPuzzlePanelStats({ userId: user.id });
        if (alive && stats) setPuzzleStats(stats);
      } catch {
        // ignore
      }
    }
    loadPuzzle();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    refreshSubscription().catch(() => {
      // ignore
    });
  }, [refreshSubscription]);

  useEffect(() => {
    if (activeTab !== 'billing') return;
    refreshSubscription().catch(() => {
      // ignore
    });

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        refreshSubscription().catch(() => {
          // ignore
        });
      }
    };

    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    return () => {
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [activeTab, refreshSubscription]);

  const mastersPath = profile?.mastersPath || computeMastersPathFromEloPoints(Number(profile?.eloPoints ?? profile?.elo_points ?? 0));
  const progressPct = Math.round(Number(mastersPath?.progressToNext || 0) * 100);
  const level = Number(profile?.computedLevel ?? mastersPath?.level ?? 1) || 1;
  const rankKey = String(profile?.computedRankKey || mastersPath?.rankKey || 'explorer');
  const rankLabel = useMemo(() => {
    const ro = { explorer: 'Explorator', apprentice: 'Ucenic', strategist: 'Strateg', champion: 'Campion', master: 'Maestru', legend: 'Legendă' };
    const en = { explorer: 'Explorer', apprentice: 'Apprentice', strategist: 'Strategist', champion: 'Champion', master: 'Master', legend: 'Legend' };
    return (isRo ? ro : en)[rankKey] || (isRo ? ro.explorer : en.explorer);
  }, [isRo, rankKey]);

  const c = isRo
    ? {
        title: 'Contul Meu',
        subtitle: 'Controlezi profilul, progresul, puzzle-urile și accesul în platformă dintr-un singur loc.',
        signInTitle: 'Autentificare necesară',
        signInBody: 'Pentru a vedea și edita detaliile de cont, autentifică-te.',
        signInBtn: 'Autentificare',
        back: 'Înapoi',
        tabs: {
          overview: 'Overview',
          progress: 'Progres',
          puzzles: 'Puzzle-uri',
          billing: 'Abonament',
          security: 'Securitate'
        },
        profileCard: 'Profil',
        alias: 'Alias',
        email: 'Email',
        language: 'Limbă',
        timezone: 'Time zone',
        save: 'Salvează alias',
        logout: 'Logout',
        saving: 'Salvez...',
        progressTitle: 'Master Path',
        toNext: 'până la nivelul următor',
        puzzleTitle: 'Statistici Puzzle',
        billingTitle: 'Plan curent',
        billingBody: 'Alege planul potrivit pentru ritmul tău: gratuit, starter promo sau premium complet.',
        billingLaunch: 'Oferta de lansare este activă: primii 500 membri blochează prețul special pe termen lung. Nu rata fereastra de acces.',
        billingCta: 'Upgrade',
        billingCancel: 'Anulează planul',
        billingChanging: 'Actualizăm abonamentul...',
        billingSwitching: 'Schimbăm planul...',
        billingCancelDone: 'Abonamentul a fost programat pentru închidere la următoarea reînnoire.',
        billingCancelScheduled: 'Anularea este programată pentru finalul ciclului curent.',
        billingCancelScheduledWithDate: 'Anularea este programată la data de',
        billingSwitchDone: 'Schimbarea de plan a fost înregistrată.',
        billingMissingConfig: 'Checkout-ul este temporar indisponibil. Te rugăm să încerci din nou în câteva minute.',
        billingOpening: 'Se deschide checkout-ul securizat...',
        billingAfterCheckout: 'După plată, planul se actualizează automat în câteva secunde.',
        billingCancelConfirm: 'Confirmi anularea abonamentului? Accesul rămâne activ până la următoarea reînnoire.',
        billingDowngradeConfirm: 'Confirmi downgrade-ul? Schimbarea se aplică la următoarea reînnoire, fără taxare imediată.',
        renewalDate: 'Data reînnoire',
        securityTitle: 'Setări de securitate',
        securityBody: 'Actualizează parola direct din cont pentru a-ți menține accesul securizat.',
        changePasswordTitle: 'Schimbă parola',
        newPassword: 'Parolă nouă',
        confirmPassword: 'Confirmă parola nouă',
        updatePassword: 'Actualizează parola',
        passwordMinLength: 'Parola trebuie să aibă cel puțin 8 caractere.',
        passwordMismatch: 'Parolele nu coincid.',
        resetEmailSent: 'Emailul de resetare a fost trimis. Verifică inbox-ul.',
        passwordChanged: 'Parola a fost actualizată cu succes.',
        securityWorking: 'Procesăm cererea...',
        securityActionFailed: 'Nu am putut finaliza acțiunea. Încearcă din nou.',
        globalRank: 'Rank general',
        solved: 'Rezolvate',
        accuracy: 'Acuratețe',
        puzzleRating: 'Puzzle Rating',
        puzzlePoints: 'Puzzle Points',
        iqPoints: 'IQ Points',
        level: 'Nivel',
        saved: 'Alias salvat.'
      }
    : {
        title: 'My Account',
        subtitle: 'Manage profile, progress, puzzle stats, and access settings from one place.',
        signInTitle: 'Sign in required',
        signInBody: 'Sign in to view and edit account details.',
        signInBtn: 'Sign in',
        back: 'Back',
        tabs: {
          overview: 'Overview',
          progress: 'Progress',
          puzzles: 'Puzzles',
          billing: 'Billing',
          security: 'Security'
        },
        profileCard: 'Profile',
        alias: 'Nickname',
        email: 'Email',
        language: 'Language',
        timezone: 'Time zone',
        save: 'Save nickname',
        logout: 'Sign out',
        saving: 'Saving...',
        progressTitle: "Master's Path",
        toNext: 'to next level',
        puzzleTitle: 'Puzzle stats',
        billingTitle: 'Current plan',
        billingBody: 'Choose the right plan for your pace: free, promo starter, or full premium.',
        billingLaunch: 'Launch offer is live: the first 500 members lock special pricing long-term. Do not miss this window.',
        billingCta: 'Upgrade',
        billingCancel: 'Cancel plan',
        billingChanging: 'Updating subscription...',
        billingSwitching: 'Switching plan...',
        billingCancelDone: 'Your subscription is scheduled to end at the next renewal.',
        billingCancelScheduled: 'Cancellation is scheduled for the end of the current billing cycle.',
        billingCancelScheduledWithDate: 'Cancellation is scheduled on',
        billingSwitchDone: 'Your plan change has been saved.',
        billingMissingConfig: 'Checkout is temporarily unavailable. Please try again in a few minutes.',
        billingOpening: 'Opening secure checkout...',
        billingAfterCheckout: 'After payment, your plan updates automatically within seconds.',
        billingCancelConfirm: 'Confirm cancellation? Access remains active until the next renewal date.',
        billingDowngradeConfirm: 'Confirm downgrade? The change applies at next renewal, with no immediate charge.',
        renewalDate: 'Renewal date',
        securityTitle: 'Security settings',
        securityBody: 'Update your password directly from your account to keep your access secure.',
        changePasswordTitle: 'Change password',
        newPassword: 'New password',
        confirmPassword: 'Confirm new password',
        updatePassword: 'Update password',
        passwordMinLength: 'Password must be at least 8 characters.',
        passwordMismatch: 'Passwords do not match.',
        resetEmailSent: 'Reset email sent. Please check your inbox.',
        passwordChanged: 'Password updated successfully.',
        securityWorking: 'Processing...',
        securityActionFailed: 'We could not complete this action. Please try again.',
        globalRank: 'Global rank',
        solved: 'Solved',
        accuracy: 'Accuracy',
        puzzleRating: 'Puzzle Rating',
        puzzlePoints: 'Puzzle Points',
        iqPoints: 'IQ Points',
        level: 'Level',
        saved: 'Nickname saved.'
      };
  const pricingCards = useMemo(() => getPlanUiCards(language), [language]);
  const hasActivePaddlePlan = planProvider === 'paddle' && ['active', 'trialing'].includes(planStatus);
  const mapProfileSaveError = useCallback((err) => {
    const raw = String(err?.message || err || '').toLowerCase();
    if (!raw) {
      return language === 'ro'
        ? 'Nu am putut salva modificările acum. Te rugăm să încerci din nou.'
        : 'We could not save your changes right now. Please try again.';
    }
    if (raw.includes('duplicate') || raw.includes('already')) {
      return language === 'ro'
        ? 'Aliasul este deja folosit. Alege unul diferit.'
        : 'This nickname is already used. Please choose another one.';
    }
    if (raw.includes('network') || raw.includes('failed to fetch') || raw.includes('timeout')) {
      return language === 'ro'
        ? 'Conexiunea este instabilă. Încearcă din nou.'
        : 'Connection is unstable. Please try again.';
    }
    if (raw.includes('unauthorized') || raw.includes('invalid jwt') || raw.includes('forbidden')) {
      return language === 'ro'
        ? 'Sesiunea a expirat. Reautentifică-te și încearcă din nou.'
        : 'Your session has expired. Please sign in again and retry.';
    }
    return language === 'ro'
      ? 'Nu am putut salva aliasul acum. Te rugăm să încerci din nou.'
      : 'We could not save your nickname right now. Please try again.';
  }, [language]);
  const mapBillingErrorMessage = useCallback((err) => {
    const raw = String(err?.message || err || '').toLowerCase();
    if (!raw) return c.billingMissingConfig;
    if (raw.includes('insufficient_funds') || raw.includes('declined')) {
      return language === 'ro'
        ? 'Plata nu a fost aprobată. Verifică detaliile cardului sau încearcă altă metodă de plată.'
        : 'Payment was not approved. Check your card details or try another payment method.';
    }
    if (raw.includes('invalid jwt') || raw.includes('unauthorized') || raw.includes('forbidden')) {
      return language === 'ro'
        ? 'Sesiunea ta a expirat. Reautentifică-te și încearcă din nou.'
        : 'Your session has expired. Please sign in again and retry.';
    }
    if (raw.includes('network') || raw.includes('failed to fetch') || raw.includes('timeout')) {
      return language === 'ro'
        ? 'Conexiunea este instabilă momentan. Te rugăm să încerci din nou.'
        : 'Connection is unstable right now. Please try again.';
    }
    return language === 'ro'
      ? 'Nu am putut finaliza acțiunea acum. Te rugăm să încerci din nou în câteva minute.'
      : 'We could not complete this action right now. Please try again in a few minutes.';
  }, [c.billingMissingConfig, language]);
  const renewalDateFormatted = useMemo(() => {
    if (!planPeriodEnd) return '';
    const d = new Date(planPeriodEnd);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-US');
  }, [language, planPeriodEnd]);
  const cancelScheduleStorageKey = useMemo(
    () => (user?.id ? `billing_cancel_scheduled_${user.id}` : ''),
    [user?.id]
  );
  const cancellationScheduled = useMemo(() => {
    if (planCancelAtPeriodEnd) return true;
    if (!cancelScheduleStorageKey) return false;
    try {
      const raw = window.localStorage.getItem(cancelScheduleStorageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      const storedSubId = String(parsed?.subscriptionId || '');
      if (!storedSubId) return false;
      return (
        planProvider === 'paddle' &&
        ['active', 'trialing'].includes(planStatus) &&
        !!planExternalSubscriptionId &&
        planExternalSubscriptionId === storedSubId
      );
    } catch {
      return false;
    }
  }, [cancelScheduleStorageKey, planProvider, planStatus, planExternalSubscriptionId, planCancelAtPeriodEnd]);

  useEffect(() => {
    if (!cancelScheduleStorageKey) return;
    if (planStatus === 'canceled' || planProvider !== 'paddle') {
      window.localStorage.removeItem(cancelScheduleStorageKey);
    }
  }, [cancelScheduleStorageKey, planProvider, planStatus]);

  if (!user) {
    return (
      <main className="account-page-wrap">
        <section className="account-page-shell account-page-empty">
          <button type="button" className="btn-back-home" onClick={onBack}>
            ← {c.back}
          </button>
          <h1>{c.signInTitle}</h1>
          <p>{c.signInBody}</p>
          <button type="button" className="btn-primary-solid" onClick={onRequestLogin}>
            {c.signInBtn}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="account-page-wrap">
      <section className="account-page-shell">
        <div className="account-page-top">
          <button type="button" className="btn-back-home" onClick={onBack}>
            ← {c.back}
          </button>
          <h1>{c.title}</h1>
          <p>{c.subtitle}</p>
        </div>

        <div className="account-page-layout">
          <aside className="account-page-side">
            <div className="account-user-badge">
              <div className="account-avatar">{String(nickname || 'U').slice(0, 1).toUpperCase()}</div>
              <div>
                <h3>{nickname || user.email}</h3>
                <p>{user.email}</p>
              </div>
            </div>
            <nav className="account-tabs-nav">
              {Object.keys(c.tabs).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`account-tab-btn ${activeTab === key ? 'active' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  {c.tabs[key]}
                </button>
              ))}
            </nav>
          </aside>

          <div className="account-page-main">
            {activeTab === 'overview' && (
              <section className="account-card">
                <h2>{c.profileCard}</h2>
                <div className="account-grid">
                  <label>
                    <span>{c.alias}</span>
                    <input
                      value={nicknameDraft}
                      onChange={(e) => setNicknameDraft(e.target.value)}
                      autoComplete="nickname"
                      placeholder={isRo ? 'ex: Dudu' : 'e.g. Dudu'}
                    />
                  </label>
                  <label>
                    <span>{c.email}</span>
                    <input value={String(user.email || '')} disabled />
                  </label>
                  <label>
                    <span>{c.language}</span>
                    <select value={language} onChange={(e) => onLanguageChange?.(e.target.value)}>
                      <option value="ro">RO</option>
                      <option value="en">EN</option>
                    </select>
                  </label>
                  <label>
                    <span>{c.timezone}</span>
                    <input value={Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'} disabled />
                  </label>
                </div>
                {(saveErr || saveMsg) ? <div className={`account-msg ${saveErr ? 'error' : 'ok'}`}>{saveErr || saveMsg}</div> : null}
                <div className="account-actions-row">
                  <button
                    type="button"
                    className="btn-primary-solid"
                    disabled={saveBusy || !String(nicknameDraft || '').trim()}
                    onClick={async () => {
                      setSaveBusy(true);
                      setSaveErr('');
                      setSaveMsg('');
                      try {
                        await upsertMyNickname(nicknameDraft);
                        if (typeof onProfileUpdated === 'function') await onProfileUpdated();
                        setSaveMsg(c.saved);
                      } catch (e) {
                        setSaveErr(mapProfileSaveError(e));
                      } finally {
                        setSaveBusy(false);
                      }
                    }}
                  >
                    {saveBusy ? c.saving : c.save}
                  </button>
                  <button
                    type="button"
                    className="btn-outline-soft"
                    onClick={() => {
                      if (typeof onLogout === 'function') {
                        onLogout();
                        return;
                      }
                      logout();
                      onRequestLogin?.();
                    }}
                  >
                    {c.logout}
                  </button>
                </div>
              </section>
            )}

            {activeTab === 'progress' && (
              <section className="account-card">
                <h2>{c.progressTitle}</h2>
                <div className="account-stats-grid">
                  <div className="stat-box"><strong>{c.iqPoints}</strong><span>{Number(profile?.eloPoints ?? profile?.elo_points ?? 0)}</span></div>
                  <div className="stat-box"><strong>{c.level}</strong><span>{rankLabel} {level}</span></div>
                  <div className="stat-box"><strong>{c.globalRank}</strong><span>{puzzleStats.rank ? `#${puzzleStats.rank}` : '—'}</span></div>
                </div>
                <div className="account-progress-block">
                  <div className="account-progress-track"><span style={{ width: `${progressPct}%` }} /></div>
                  <p>
                    {progressPct}% · {Number(mastersPath?.pointsToNext || 0)} {isRo ? 'puncte IQ' : 'IQ Points'} {c.toNext}
                  </p>
                </div>
              </section>
            )}

            {activeTab === 'puzzles' && (
              <section className="account-card">
                <h2>{c.puzzleTitle}</h2>
                <div className="account-stats-grid">
                  <div className="stat-box"><strong>{c.puzzlePoints}</strong><span>{Number(puzzleStats.puzzlePoints || 0)}</span></div>
                  <div className="stat-box"><strong>{c.puzzleRating}</strong><span>{Number(puzzleStats.puzzleRating || 500)}</span></div>
                  <div className="stat-box"><strong>{c.globalRank}</strong><span>{puzzleStats.rank ? `#${puzzleStats.rank}` : '—'}</span></div>
                  <div className="stat-box"><strong>{c.solved}</strong><span>{Number(puzzleStats.solved || 0)}</span></div>
                  <div className="stat-box"><strong>{c.accuracy}</strong><span>{Math.round(Number(puzzleStats.accuracy || 0))}%</span></div>
                </div>
              </section>
            )}

            {activeTab === 'billing' && (
              <section className="account-card">
                <h2>{c.billingTitle}</h2>
                <div className="account-plan">
                  <div>
                    <strong>{getPlanLabel(currentPlan, language)}</strong>
                    <p>{language === 'ro' ? 'Status' : 'Status'}: <b>{planStatus}</b></p>
                    {renewalDateFormatted ? <p>{c.renewalDate}: <b>{renewalDateFormatted}</b></p> : null}
                    {cancellationScheduled ? (
                      <p>
                        <b>
                          {renewalDateFormatted
                            ? `${c.billingCancelScheduledWithDate} ${renewalDateFormatted}.`
                            : c.billingCancelScheduled}
                        </b>
                      </p>
                    ) : null}
                    <p>{c.billingBody}</p>
                  </div>
                  <button
                    type="button"
                    className="btn-outline-soft"
                    disabled={!hasActivePaddlePlan || manageBusy === 'cancel'}
                    onClick={async () => {
                      if (!hasActivePaddlePlan) return;
                      const confirmText = renewalDateFormatted
                        ? `${c.billingCancelConfirm}\n${c.renewalDate}: ${renewalDateFormatted}`
                        : c.billingCancelConfirm;
                      if (!window.confirm(confirmText)) return;
                      try {
                        setManageBusy('cancel');
                        setCheckoutErr('');
                        setCheckoutMsg(c.billingChanging);
                        await manageMyPaddleSubscription({ action: 'cancel' });
                        if (cancelScheduleStorageKey && planExternalSubscriptionId) {
                          window.localStorage.setItem(
                            cancelScheduleStorageKey,
                            JSON.stringify({
                              subscriptionId: planExternalSubscriptionId,
                              savedAt: new Date().toISOString()
                            })
                          );
                        }
                        const renewalPart = renewalDateFormatted ? ` ${c.renewalDate}: ${renewalDateFormatted}.` : '';
                        setCheckoutMsg(`${c.billingCancelDone}${renewalPart}`.trim());
                        await refreshSubscription();
                      } catch (e) {
                        setCheckoutErr(mapBillingErrorMessage(e));
                        setCheckoutMsg('');
                      } finally {
                        setManageBusy('');
                      }
                    }}
                  >
                    {hasActivePaddlePlan ? c.billingCancel : c.billingCta}
                  </button>
                </div>
                <div className="account-pricing-grid">
                  {pricingCards.map((card) => {
                    const active = card.code === currentPlan;
                    const canCheckout = card.code !== 'explorer' && canUsePaddleCheckout(card.code);
                    const canChangePlanInPlace = hasActivePaddlePlan && card.code !== 'explorer' && !active;
                    const planRank = (code) => {
                      if (code === 'premium') return 3;
                      if (code === 'starter') return 2;
                      return 1;
                    };
                    const isUpgrade = planRank(card.code) > planRank(currentPlan);
                    const shouldUseManagedChange = canChangePlanInPlace;
                    const isBusy = checkoutBusyPlan === card.code || manageBusy === card.code;
                    return (
                      <article key={card.code} className={`account-pricing-card ${active ? 'active' : ''}`}>
                        <h3>{card.name}</h3>
                        {card.launchLabel ? <span className="account-launch-badge">{card.launchLabel}</span> : null}
                        <div className="account-pricing-prices">
                          {card.oldPrice ? <span className="account-price-old">{card.oldPrice}</span> : null}
                          <strong>{card.newPrice}</strong>
                        </div>
                        <ul>
                          {card.features.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className="btn-primary-solid"
                          disabled={active || isBusy}
                          onClick={async () => {
                            if (active || card.code === 'explorer') return;
                            if (!shouldUseManagedChange && !canCheckout) {
                              setCheckoutErr(c.billingMissingConfig);
                              setCheckoutMsg('');
                              return;
                            }
                            try {
                              setCheckoutErr('');
                              if (shouldUseManagedChange) {
                                const confirmCopy = isUpgrade
                                  ? (language === 'ro'
                                    ? 'Confirmi upgrade-ul? Schimbarea se aplică imediat conform regulilor de facturare Paddle.'
                                    : 'Confirm upgrade? The change applies immediately according to Paddle billing rules.')
                                  : c.billingDowngradeConfirm;
                                const confirmText = renewalDateFormatted
                                  ? `${confirmCopy}\n${c.renewalDate}: ${renewalDateFormatted}`
                                  : confirmCopy;
                                if (!window.confirm(confirmText)) return;
                                setManageBusy(card.code);
                                setCheckoutMsg(c.billingSwitching);
                                await manageMyPaddleSubscription({
                                  action: 'change_plan',
                                  targetPlanCode: card.code
                                });
                                const renewalPart = renewalDateFormatted ? ` ${c.renewalDate}: ${renewalDateFormatted}.` : '';
                                setCheckoutMsg(`${c.billingSwitchDone}${renewalPart}`.trim());
                                await refreshSubscription();
                              } else {
                                setCheckoutBusyPlan(card.code);
                                setCheckoutMsg(c.billingOpening);
                                await openPlanCheckout({
                                  planCode: card.code,
                                  userId: user?.id,
                                  userEmail: user?.email,
                                  language
                                });
                                setCheckoutMsg(c.billingAfterCheckout);
                              }
                            } catch (e) {
                              setCheckoutErr(mapBillingErrorMessage(e));
                              setCheckoutMsg('');
                            } finally {
                              setCheckoutBusyPlan('');
                              setManageBusy('');
                            }
                          }}
                        >
                          {active ? (isRo ? 'Plan activ' : 'Active plan') : card.cta}
                        </button>
                      </article>
                    );
                  })}
                </div>
                {(checkoutErr || checkoutMsg) ? (
                  <div className={`account-msg ${checkoutErr ? 'error' : 'ok'}`}>{checkoutErr || checkoutMsg}</div>
                ) : null}
                <p className="account-billing-note">{c.billingLaunch}</p>
              </section>
            )}

            {activeTab === 'security' && (
              <section className="account-card">
                <h2>{c.securityTitle}</h2>
                <p>{c.securityBody}</p>
                <div className="account-grid">
                  <label>
                    <span>{c.newPassword}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </label>
                  <label>
                    <span>{c.confirmPassword}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    />
                  </label>
                </div>
                {(securityErr || securityMsg) ? (
                  <div className={`account-msg ${securityErr ? 'error' : 'ok'}`}>{securityErr || securityMsg}</div>
                ) : null}
                <div className="account-actions-row">
                  <button
                    type="button"
                    className="btn-primary-solid"
                    disabled={securityBusy}
                    onClick={async () => {
                      const candidate = String(newPassword || '');
                      if (candidate.length < 8) {
                        setSecurityErr(c.passwordMinLength);
                        setSecurityMsg('');
                        return;
                      }
                      if (candidate !== String(newPasswordConfirm || '')) {
                        setSecurityErr(c.passwordMismatch);
                        setSecurityMsg('');
                        return;
                      }
                      try {
                        setSecurityBusy(true);
                        setSecurityErr('');
                        setSecurityMsg(c.securityWorking);
                        await changeMyPassword(candidate);
                        setNewPassword('');
                        setNewPasswordConfirm('');
                        setSecurityMsg(c.passwordChanged);
                      } catch {
                        setSecurityErr(c.securityActionFailed);
                        setSecurityMsg('');
                      } finally {
                        setSecurityBusy(false);
                      }
                    }}
                  >
                    {c.updatePassword}
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

export default AccountPage;
